"use client"

import { useState } from "react"
import { DragDropContext, Droppable, Draggable, type DropResult } from "@hello-pangea/dnd"
import { Clock, Mail, MapPin, Phone, User, MessageSquare } from "lucide-react"
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog"
import { ScrollArea } from "@/components/ui/scroll-area"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { Textarea } from "@/components/ui/textarea"
import { format } from "date-fns"

// Types
type CandidateStatus =
  | "010 Open"
  | "020 Submitted"
  | "030 Internal Interviewed"
  | "040 Failed"
  | "041 Passed"
  | "050 Client Interviewing"
  | "051 Client Interviewed"
  | "060 Negotiating"
  | "070 Offer Sent"
  | "071 CAN Reject"
  | "071 CAN Accepted"
  | "080 Onboarded"
  | "090 Pending"
  | "091 Keep Contact"
  | "100 Cancelled"
  | "021 Interview Cancelled"
  | "022 CV Rejected"

interface LogEntry {
  id: string
  timestamp: Date
  type: "status" | "comment"
  content: string
  author: string
  status?: CandidateStatus
}

interface Candidate {
  id: string
  name: string
  role: string
  email: string
  phone: string
  location: string
  experience: string
  status: CandidateStatus
  avatar: string
  appliedDate: string
  lastUpdated: string
  log: LogEntry[]
}

// Mock data
const mockCandidates: Candidate[] = [
  {
    id: "1",
    name: "Alice Johnson",
    role: "Senior React Developer",
    email: "alice@example.com",
    phone: "+1234567890",
    location: "Ho Chi Minh City",
    experience: "5 years",
    status: "020 Submitted",
    avatar: "/placeholder.svg?height=40&width=40",
    appliedDate: "2024-02-25",
    lastUpdated: "2024-02-26",
    log: [
      {
        id: "log1",
        timestamp: new Date("2024-02-25T10:00:00"),
        type: "status",
        content: "Candidate application received",
        author: "System",
        status: "010 Open",
      },
      {
        id: "log2",
        timestamp: new Date("2024-02-26T11:30:00"),
        type: "status",
        content: "CV submitted for review",
        author: "John Doe (HR)",
        status: "020 Submitted",
      },
      {
        id: "log3",
        timestamp: new Date("2024-02-26T14:00:00"),
        type: "comment",
        content: "Strong React experience, potential fit for the senior role",
        author: "John Doe (HR)",
      },
    ],
  },
  {
    id: "2",
    name: "Bob Smith",
    role: "DevOps Engineer",
    email: "bob@example.com",
    phone: "+1234567891",
    location: "Ha Noi",
    experience: "3 years",
    status: "030 Internal Interviewed",
    avatar: "/placeholder.svg?height=40&width=40",
    appliedDate: "2024-02-24",
    lastUpdated: "2024-02-26",
    log: [],
  },
  {
    id: "3",
    name: "Carol White",
    role: "UI/UX Designer",
    email: "carol@example.com",
    phone: "+1234567892",
    location: "Remote",
    experience: "4 years",
    status: "041 Passed",
    avatar: "/placeholder.svg?height=40&width=40",
    appliedDate: "2024-02-23",
    lastUpdated: "2024-02-26",
    log: [],
  },
  // Add more mock candidates as needed
]

// Column definitions
const columns: { id: CandidateStatus; title: string; color: string }[] = [
  { id: "010 Open", title: "Open", color: "bg-purple-100 text-purple-800" },
  { id: "020 Submitted", title: "Submitted", color: "bg-purple-100 text-purple-800" },
  { id: "030 Internal Interviewed", title: "Internal Interviewed", color: "bg-purple-100 text-purple-800" },
  { id: "041 Passed", title: "Passed", color: "bg-purple-100 text-purple-800" },
  { id: "050 Client Interviewing", title: "Client Interviewing", color: "bg-yellow-100 text-yellow-800" },
  { id: "051 Client Interviewed", title: "Client Interviewed", color: "bg-yellow-100 text-yellow-800" },
  { id: "060 Negotiating", title: "Negotiating", color: "bg-purple-100 text-purple-800" },
  { id: "070 Offer Sent", title: "Offer Sent", color: "bg-purple-100 text-purple-800" },
  { id: "071 CAN Accepted", title: "CAN Accepted", color: "bg-purple-100 text-purple-800" },
  { id: "080 Onboarded", title: "Onboarded", color: "bg-green-100 text-green-800" },
  { id: "090 Pending", title: "Pending", color: "bg-purple-100 text-purple-800" },
  { id: "091 Keep Contact", title: "Keep Contact", color: "bg-pink-100 text-pink-800" },
  { id: "100 Cancelled", title: "Cancelled", color: "bg-red-100 text-red-800" },
]

// Candidate Card Component
function CandidateCard({ candidate }: { candidate: Candidate }) {
  return (
    <Dialog>
      <DialogTrigger asChild>
        <Card className="mb-2 cursor-pointer hover:border-primary/50 transition-colors">
          <CardHeader className="p-4 pb-2">
            <div className="flex items-start justify-between">
              <div className="flex items-center space-x-3">
                <Avatar>
                  <AvatarImage src={candidate.avatar} alt={candidate.name} />
                  <AvatarFallback>{candidate.name.slice(0, 2).toUpperCase()}</AvatarFallback>
                </Avatar>
                <div>
                  <CardTitle className="text-sm font-medium">{candidate.name}</CardTitle>
                  <CardDescription className="text-xs">{candidate.role}</CardDescription>
                </div>
              </div>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="h-8 w-8 p-0">
                    <span className="sr-only">Open menu</span>
                    <svg
                      className="h-4 w-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z"
                      />
                    </svg>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuLabel>Actions</DropdownMenuLabel>
                  <DropdownMenuItem>View Profile</DropdownMenuItem>
                  <DropdownMenuItem>Schedule Interview</DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem className="text-red-600">Reject</DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </CardHeader>
          <CardContent className="p-4 pt-2">
            <div className="flex items-center justify-between text-xs text-muted-foreground">
              <div className="flex items-center space-x-3">
                <div className="flex items-center">
                  <MapPin className="mr-1 h-3 w-3" />
                  {candidate.location}
                </div>
                <div className="flex items-center">
                  <Clock className="mr-1 h-3 w-3" />
                  {candidate.experience}
                </div>
              </div>
              <div className="flex items-center">
                <MessageSquare className="mr-1 h-3 w-3" />
                {candidate.log.length}
              </div>
            </div>
          </CardContent>
        </Card>
      </DialogTrigger>
      <DialogContent className="max-w-4xl max-h-[80vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle>Candidate Profile</DialogTitle>
          <DialogDescription>View and manage candidate information</DialogDescription>
        </DialogHeader>
        <Tabs defaultValue="profile" className="flex-grow overflow-hidden flex flex-col">
          <TabsList>
            <TabsTrigger value="profile">Profile</TabsTrigger>
            <TabsTrigger value="log">Activity Log</TabsTrigger>
          </TabsList>
          <TabsContent value="profile" className="flex-grow overflow-auto">
            <div className="grid gap-6">
              <div className="flex items-start space-x-4">
                <Avatar className="h-16 w-16">
                  <AvatarImage src={candidate.avatar} alt={candidate.name} />
                  <AvatarFallback>{candidate.name.slice(0, 2).toUpperCase()}</AvatarFallback>
                </Avatar>
                <div className="space-y-1">
                  <h3 className="text-xl font-medium">{candidate.name}</h3>
                  <p className="text-muted-foreground">{candidate.role}</p>
                  <div className="flex space-x-4">
                    <div className="flex items-center text-sm">
                      <Mail className="mr-2 h-4 w-4" />
                      {candidate.email}
                    </div>
                    <div className="flex items-center text-sm">
                      <Phone className="mr-2 h-4 w-4" />
                      {candidate.phone}
                    </div>
                  </div>
                </div>
              </div>

              <div className="grid gap-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <h4 className="font-medium">Location</h4>
                    <p className="text-sm text-muted-foreground">{candidate.location}</p>
                  </div>
                  <div className="space-y-2">
                    <h4 className="font-medium">Experience</h4>
                    <p className="text-sm text-muted-foreground">{candidate.experience}</p>
                  </div>
                  <div className="space-y-2">
                    <h4 className="font-medium">Applied Date</h4>
                    <p className="text-sm text-muted-foreground">{candidate.appliedDate}</p>
                  </div>
                  <div className="space-y-2">
                    <h4 className="font-medium">Last Updated</h4>
                    <p className="text-sm text-muted-foreground">{candidate.lastUpdated}</p>
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <h4 className="font-medium">Current Status</h4>
                <Badge variant="secondary" className={columns.find((col) => col.id === candidate.status)?.color}>
                  {candidate.status}
                </Badge>
              </div>
            </div>
          </TabsContent>
          <TabsContent value="log" className="flex-grow overflow-hidden flex flex-col">
            <ScrollArea className="flex-grow">
              <div className="space-y-4">
                {candidate.log.map((entry) => (
                  <Card key={entry.id}>
                    <CardHeader className="p-4 pb-2">
                      <div className="flex justify-between items-start">
                        <div>
                          <CardTitle className="text-sm font-medium">
                            {entry.type === "status" ? "Status Update" : "Comment"}
                          </CardTitle>
                          <CardDescription className="text-xs">
                            {format(entry.timestamp, "PPpp")} by {entry.author}
                          </CardDescription>
                        </div>
                        {entry.type === "status" && (
                          <Badge variant="secondary" className={columns.find((col) => col.id === entry.status)?.color}>
                            {entry.status}
                          </Badge>
                        )}
                      </div>
                    </CardHeader>
                    <CardContent className="p-4 pt-2">
                      <p className="text-sm">{entry.content}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </ScrollArea>
            <div className="mt-4 border-t pt-4">
              <Textarea placeholder="Add a comment..." className="mb-2" />
              <Button>Add Comment</Button>
            </div>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  )
}

export default function RecruitmentKanban() {
  const [candidates, setCandidates] = useState(mockCandidates)

  const onDragEnd = (result: DropResult) => {
    const { destination, source, draggableId } = result

    if (!destination) return

    if (destination.droppableId === source.droppableId && destination.index === source.index) {
      return
    }

    const updatedCandidates = candidates.map((candidate) => {
      if (candidate.id === draggableId) {
        const newStatus = destination.droppableId as CandidateStatus
        const newLog: LogEntry = {
          id: `log-${Date.now()}`,
          timestamp: new Date(),
          type: "status",
          content: `Status updated from ${candidate.status} to ${newStatus}`,
          author: "System",
          status: newStatus,
        }
        return {
          ...candidate,
          status: newStatus,
          log: [...candidate.log, newLog],
          lastUpdated: format(new Date(), "yyyy-MM-dd"),
        }
      }
      return candidate
    })

    setCandidates(updatedCandidates)
  }

  return (
    <div className="h-full">
      <div className="mb-4 flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold tracking-tight">Recruitment Pipeline</h2>
          <p className="text-muted-foreground">Drag and drop candidates to update their status</p>
        </div>
        <Button>
          <User className="mr-2 h-4 w-4" />
          Add Candidate
        </Button>
      </div>

      <DragDropContext onDragEnd={onDragEnd}>
        <div className="flex space-x-4 overflow-x-auto pb-4">
          {columns.map((column) => (
            <div key={column.id} className="flex-none w-80">
              <div className="mb-3 flex items-center justify-between">
                <h3 className="font-semibold text-sm">
                  {column.title}
                  <Badge variant="secondary" className="ml-2">
                    {candidates.filter((c) => c.status === column.id).length}
                  </Badge>
                </h3>
              </div>

              <Droppable droppableId={column.id}>
                {(provided) => (
                  <div
                    {...provided.droppableProps}
                    ref={provided.innerRef}
                    className="bg-muted/30 rounded-lg p-2 min-h-[500px]"
                  >
                    {candidates
                      .filter((candidate) => candidate.status === column.id)
                      .map((candidate, index) => (
                        <Draggable key={candidate.id} draggableId={candidate.id} index={index}>
                          {(provided) => (
                            <div ref={provided.innerRef} {...provided.draggableProps} {...provided.dragHandleProps}>
                              <CandidateCard candidate={candidate} />
                            </div>
                          )}
                        </Draggable>
                      ))}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            </div>
          ))}
        </div>
      </DragDropContext>
    </div>
  )
}

